/**
 * チバリヨ - メインエントリ (Phase 2-B-5 / ホーム画面リニューアル版)
 *
 * Phase 2-B-5 の変更点:
 *   - ホーム画面を welcome カードから「天気 + 日替わり名言」(home.js) に刷新
 *   - ホームは admin / sales 共通で表示 (PC シェルの初期画面)
 *   - 初回表示時のみ 5秒後に自動遷移 (admin -> P1 / sales -> P7)。
 *     ナビの「ホーム」クリック時は自動遷移しない
 *
 * Phase 2-B-4 の変更点:
 *   - P7 営業ダッシュボードを追加 (sales / admin が PC で開ける)
 *   - sales も PC シェルを利用可能に:
 *       - 幅 768px 未満 → 常にモバイル (スマホで PC 画面が固定される事故防止)
 *       - シェル設定 (localStorage) 保存済み → それに従う
 *       - 未保存 → 幅 1024px 以上なら PC (P7) / 未満ならモバイル
 *   - sales の PC ナビは P7 のみ。「モバイル表示」「PC画面」で相互切替 (記憶される)
 *
 * Phase 2-B-0 後半の変更点:
 *   - sales ロールでログインした場合は mobile-shell.js を呼び出し、
 *     スマホ向け画面シェルを表示する
 *   - admin / viewer は既存通り (app-view を使う)
 *   - 既存の admin/viewer ロジックには一切手を入れない (回帰リスクゼロ方針)
 *
 * Phase 2-A-4 → Phase 2-B-0 前半の変更点 (前回):
 *   - registerServiceWorker() を追加
 *   - paintEnvBadge() を拡張し、login画面・app画面の両 DEVバッジを集約制御
 *
 * ナビゲーション (admin):
 *   - ホーム / ダッシュボード (P1) / メンバー管理 (P3) / 目標設定 (P2)
 *   - 顧客マスタ管理 (P4) / システム設定 (P6)
 */

import { onAuthStateChanged } from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-auth.js';
import { auth, authReady, CHIBARIYO_ENV } from './firebase-init.js';
import { APP_BUILD, APP_VERSION_FULL } from './version.js';
import {
  signInWithGoogle,
  sendMagicLink,
  completeMagicLinkSignIn,
  completeMagicLinkSignInWithEmail,
  linkMemberOnLogin,
  signOutChibariyo,
  getMemberById,
  listMembers,
  ChibariyoAuthError
} from './auth.js';
import { renderMembersPage } from './p3-members.js';
import { renderConfigPage } from './p6-config.js';
import { renderGoalsPage } from './p2-goals.js';
import { renderClientsPage } from './p4-clients.js';
import { renderDashboardPage } from './p1-dashboard.js';
import { renderSalesDashboardPage } from './p7-dashboard.js';
import { renderHomePage } from './home.js';
import { renderMobileShell } from './mobile-shell.js';
import { initPush, maybeShowEnableBanner } from './push.js';

const $ = (id) => document.getElementById(id);
const loginView = () => $('login-view');
const appView = () => $('app-view');
const mobileView = () => $('mobile-app-view');
const loadingView = () => $('loading-view');

const googleBtn = () => $('google-signin-btn');
const emailInput = () => $('email-input');
const emailLinkBtn = () => $('email-link-btn');
const loginMsg = () => $('login-message');
const loginError = () => $('login-error');

const userNameEl = () => $('app-user-name');
const userRoleEl = () => $('app-user-role');
const userAvatarEl = () => $('app-user-avatar');
const signOutBtn = () => $('sign-out-btn');

// 各ナビボタンとコンテンツコンテナ (admin/viewer/sales-PC 用)
const NAV_ITEMS = ['home', 'p1', 'p7', 'p3', 'p2', 'p4', 'p6'];

const FALLBACK_AVATAR_COLORS = {
  blue:   { text: '#0C447C', bg: '#E6F1FB' },
  teal:   { text: '#0F6E56', bg: '#E1F5EE' },
  amber:  { text: '#854F0B', bg: '#FAEEDA' },
  purple: { text: '#3C3489', bg: '#EEEDFE' },
  coral:  { text: '#A32D2D', bg: '#FCEBEB' },
  slate:  { text: '#475569', bg: '#F1F5F9' },
  rose:   { text: '#9F1239', bg: '#FFE4E6' },
  indigo: { text: '#3730A3', bg: '#E0E7FF' }
};

const ROLE_LABEL = { admin: '管理者', sales: '営業', viewer: '閲覧者' };

let currentSessionMember = null;

// Phase 2-B-5.1: dev admin プレビュー中の「見ているメンバー」。
//   通常ログイン時は null。?as=<memberId> プレビュー時のみ対象メンバーが入る。
//   navigateTo はこの値があれば優先して使う (currentSessionMember は粟村さんのまま = 認証は不変)。
let previewMember = null;
function activeMember() {
  return previewMember || currentSessionMember;
}

/* ========================================================================
 * シェル切替設定 (Phase 2-B-2.1)
 *   admin の PC ⇄ モバイル切替を localStorage に記憶する (端末ごと)
 * ====================================================================== */

const SHELL_PREF_PREFIX = 'chibariyo_shell_pref_';

function getShellPref(memberId) {
  try {
    return window.localStorage.getItem(SHELL_PREF_PREFIX + memberId) || 'pc';
  } catch (e) {
    return 'pc';
  }
}

/** Phase 2-B-4: 未保存 (null) を区別したい sales 分岐用 */
function getShellPrefRaw(memberId) {
  try {
    return window.localStorage.getItem(SHELL_PREF_PREFIX + memberId);
  } catch (e) {
    return null;
  }
}

function setShellPref(memberId, value) {
  try {
    window.localStorage.setItem(SHELL_PREF_PREFIX + memberId, value);
  } catch (e) { /* noop */ }
}

/* ========================================================================
 * ビュー切替
 * ====================================================================== */

function hideAllViews() {
  loginView().style.display = 'none';
  appView().style.display = 'none';
  if (mobileView()) mobileView().style.display = 'none';
  loadingView().style.display = 'none';
}

function showLogin() {
  hideAllViews();
  loginView().style.display = 'flex';
}

function showPcApp() {
  hideAllViews();
  appView().style.display = 'block';
}

function showMobileApp() {
  hideAllViews();
  if (mobileView()) mobileView().style.display = 'flex';
}

function showLoading() {
  hideAllViews();
  loadingView().style.display = 'flex';
}

function setLoginMessage(text, isError = false) {
  const errEl = loginError();
  const msgEl = loginMsg();
  if (isError) {
    errEl.textContent = text;
    errEl.style.display = text ? 'block' : 'none';
    msgEl.textContent = '';
    msgEl.style.display = 'none';
  } else {
    msgEl.textContent = text;
    msgEl.style.display = text ? 'block' : 'none';
    errEl.textContent = '';
    errEl.style.display = 'none';
  }
}

/**
 * DEVバッジ表示制御 (login画面・app画面の両方を集約制御)
 * モバイル画面の DEVバッジは mobile-shell.js 側で処理する。
 */
function paintEnvBadge() {
  const ids = ['env-badge', 'env-badge-app'];
  for (const id of ids) {
    const el = $(id);
    if (!el) continue;
    if (CHIBARIYO_ENV === 'dev') {
      el.textContent = 'DEV';
      el.style.display = 'inline-block';
    } else {
      el.style.display = 'none';
    }
  }
}

/** バージョンを表示 (ログイン画面隅 + PCシェルの共通フッタ) */
function paintVersionLabel() {
  const loginEl = $('login-version');
  if (loginEl) loginEl.textContent = APP_VERSION_FULL;
  const footerEl = $('app-footer-version');
  if (footerEl) footerEl.textContent = `チバリヨ ${APP_VERSION_FULL}`;
}

/**
 * Service Worker 登録 (network-first戦略)
 */
function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((reg) => console.log('[チバリヨ] SW登録:', reg.scope))
      .catch((err) => console.warn('[チバリヨ] SW登録失敗:', err));
  });
}

/* ========================================================================
 * バージョン更新バナー
 *   自分のビルド (APP_BUILD, ビルド時に焼き込み) と、配信中の /version.json の
 *   build を突き合わせ、異なれば新デプロイとみなしてバナーを出す。
 *   version.json は build.mjs が生成し no-cache 配信されるため、デプロイのたびに
 *   自動で更新検知される (Firestore の手動 +1 は不要)。
 * ====================================================================== */
let versionWatchStarted = false;
let versionBannerShown = false;

async function readServedBuild() {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.build ? data.build : null;
  } catch (e) {
    return null; // 読めない時は判定しない
  }
}

async function startVersionWatch() {
  if (versionWatchStarted) return; // 再ログイン等で多重起動しない
  versionWatchStarted = true;
  // タブが見える状態に戻った時 + 10分ごとにチェック
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkAppVersion();
  });
  setInterval(checkAppVersion, 10 * 60 * 1000);
}

async function checkAppVersion() {
  if (versionBannerShown) return;
  if (APP_BUILD === 'local') return; // 未ビルド(開発)配信では判定しない
  const served = await readServedBuild();
  if (served === null) return;
  // 配信中ビルドが自分のビルドと違う = 新デプロイ
  if (served !== APP_BUILD) showVersionBanner();
}

function showVersionBanner() {
  if (versionBannerShown) return;
  versionBannerShown = true;

  const bar = document.createElement('div');
  bar.id = 'app-update-banner';
  bar.style.cssText = [
    'position:fixed', 'left:50%', 'bottom:16px', 'transform:translateX(-50%)',
    'z-index:99999', 'display:flex', 'align-items:center', 'gap:12px',
    'max-width:calc(100vw - 24px)', 'padding:10px 14px',
    'background:#1A202C', 'color:#fff', 'border-radius:10px',
    'box-shadow:0 6px 24px rgba(0,0,0,0.25)', 'font-size:14px',
  ].join(';');
  bar.innerHTML = `
    <span style="line-height:1.4;">新しいバージョンがあります</span>
    <button type="button" style="flex-shrink:0;font-size:13px;font-weight:700;color:#1A202C;background:#fff;border:none;border-radius:6px;padding:6px 14px;cursor:pointer;">更新</button>
  `;
  bar.querySelector('button').addEventListener('click', () => {
    // SW のHTTPキャッシュも確実に外して最新を取得
    location.reload();
  });
  document.body.appendChild(bar);
}

/* ========================================================================
 * ロール別シェル
 * ====================================================================== */

/**
 * メンバー紐付け完了後に呼ばれる。ロールで分岐:
 *   - sales -> モバイルシェル
 *   - admin / viewer -> 既存PCシェル
 */

function applyRoleShell(member) {
  // dev環境 + admin 限定: ?as=... でメンバープレビュー
  //   - ?as=sales            : 従来どおり (粟村さん自身のオブジェクトで sales モバイルシェル)
  //   - ?as=<memberId>        : 指定メンバーの画面を「閲覧専用」でプレビュー
  //   - ?as= / ?as=list       : dev メンバー一覧ピッカー
  // ※ プレビューは閲覧専用 (FAB「+」を非表示)。ただしルール上 admin は他人の
  //    reports/tasks を update できるため、編集導線などは「触らない」前提の割り切り。
  const asParam =
    (CHIBARIYO_ENV === 'dev' && member.role === 'admin')
      ? new URLSearchParams(window.location.search).get('as')
      : null;

  if (asParam !== null) {
    const view = new URLSearchParams(window.location.search).get('view');
    startAdminPreview(asParam, member, view);
    return;
  }

  if (member.role === 'sales') {
    // Phase 2-B-4: sales は PC 幅では P7 (PC シェル) を表示できる。
    //   - 幅 768px 未満 → 常にモバイル (pref を無視。スマホで PC 画面が残る事故防止)
    //   - pref 保存済み → それに従う
    //   - pref 未保存 → 幅 1024px 以上なら PC / 未満ならモバイル
    const width = window.innerWidth;
    const pref = getShellPrefRaw(member.memberId);
    const useMobile =
      width < 768 ||
      pref === 'mobile' ||
      (pref === null && width < 1024);

    if (useMobile) {
      renderMobileShell(mobileView(), member, CHIBARIYO_ENV, {
        // PC 切替ボタンは PC 表示が成立する幅でのみ出す
        showPcSwitch: true,
        onSwitchToPc: () => {
          setShellPref(member.memberId, 'pc');
          window.location.reload();
        }
      });
      showMobileApp();
    } else {
      renderPcShell(member);
      showPcApp();
    }
    return;
  }

  // Phase 2-B-2.1: admin はモバイルシェルを選択でき、選択は端末ごとに記憶される。
  // モバイルシェル側に「PC画面へ」切替ボタンを表示する。
  if (member.role === 'admin' && getShellPref(member.memberId) === 'mobile') {
    renderMobileShell(mobileView(), member, CHIBARIYO_ENV, {
      showPcSwitch: true,
      onSwitchToPc: () => {
        setShellPref(member.memberId, 'pc');
        window.location.reload();
      }
    });
    showMobileApp();
    return;
  }

  renderPcShell(member);
  showPcApp();
}

/* ========================================================================
 * dev admin メンバープレビュー (Phase 2-B-5.1)
 *   ?as=<memberId> で指定メンバーのモバイルシェルを「閲覧専用」で表示。
 *   ?as= / ?as=list でメンバー一覧ピッカーを表示。
 *   dev + admin でしか呼ばれない (applyRoleShell でガード済み)。
 * ====================================================================== */
async function startAdminPreview(asParam, adminMember, view) {
  showLoading();
  try {
    if (asParam === '' || asParam === 'list') {
      const members = await listMembers();
      renderPreviewPicker(members);
      return;
    }

    // ?as=sales は後方互換: 粟村さん自身のオブジェクトをプレビュー対象にする
    let target;
    if (asParam === 'sales') {
      target = adminMember;
    } else {
      target = await getMemberById(asParam);
      if (!target) {
        renderPreviewError(`memberId「${asParam}」のメンバーが見つかりませんでした。`);
        return;
      }
    }

    // navigateTo / 各ページが activeMember() = previewMember を見てデータを引く。
    // 認証 (currentSessionMember / request.auth) は粟村さんのまま = 書き込み権限は不変。
    previewMember = target;

    if (view === 'pc') {
      // PC プレビュー: 対象メンバーの PC シェル。
      //   sales 対象なら home + P7 のみ (読み取り専用)。
      //   ナビ/データは renderPcShell(target) + activeMember() で対象メンバーに揃う。
      renderPcShell(target);
      // プレビューであることを明示 (ロール表示を流用)
      const roleEl = userRoleEl();
      if (roleEl) {
        roleEl.textContent = `プレビュー(${ROLE_LABEL[target.role] || target.role || ''})`;
      }
      showPcApp();
      return;
    }

    // モバイルプレビュー (既定): 閲覧専用 + PC 切替ボタン付き。
    // S1/S2/S4/S5 は渡した member の memberId でデータを引くため、本人の実データが出る。
    renderMobileShell(mobileView(), target, CHIBARIYO_ENV, {
      hideFab: true,
      previewBadge: `プレビュー: ${escapeHtml(target.name || target.memberId)}(閲覧専用)`,
      showPcSwitch: true,
      onSwitchToPc: () => gotoPreview(target.memberId, 'pc'),
    });
    showMobileApp();
  } catch (err) {
    console.error('[チバリヨ] プレビュー失敗:', err);
    renderPreviewError(err && err.message ? err.message : String(err));
  }
}

/** プレビューの URL を組み替えて遷移 (as と view を更新) */
function gotoPreview(memberId, view) {
  const u = new URLSearchParams(window.location.search);
  u.set('as', memberId);
  if (view) {
    u.set('view', view);
  } else {
    u.delete('view');
  }
  window.location.search = u.toString();
}

const ROLE_LABEL_JA = { admin: '管理者', sales: '営業', viewer: '閲覧' };

function renderPreviewPicker(members) {
  const mv = mobileView();
  if (!mv) return;
  const items = members
    .slice()
    .sort((a, b) => (a.role || '').localeCompare(b.role || ''))
    .map((m) => {
      const name = escapeHtml(m.name || '(名前未設定)');
      const role = ROLE_LABEL_JA[m.role] || m.role || '';
      const active = m.active === false ? ' / 無効' : '';
      const id = escapeHtml(m.memberId);
      return `
        <a class="preview-picker-item" href="?as=${encodeURIComponent(m.memberId)}">
          <span class="preview-picker-name">${name}</span>
          <span class="preview-picker-meta">${role}${active}</span>
          <span class="preview-picker-id">${id}</span>
        </a>`;
    })
    .join('');

  mv.innerHTML = `
    <div class="preview-picker">
      <h2 class="preview-picker-title">プレビューするメンバー</h2>
      <p class="preview-picker-note">dev 限定 / 閲覧専用。選ぶとそのメンバーの画面を表示します。</p>
      <div class="preview-picker-list">${items}</div>
    </div>`;
  showMobileApp();
}

function renderPreviewError(message) {
  const mv = mobileView();
  if (!mv) return;
  mv.innerHTML = `
    <div class="preview-picker">
      <h2 class="preview-picker-title">プレビューできません</h2>
      <p class="preview-picker-note">${escapeHtml(message)}</p>
      <div class="preview-picker-list">
        <a class="preview-picker-item" href="?as=list">
          <span class="preview-picker-name">メンバー一覧へ</span>
        </a>
      </div>
    </div>`;
  showMobileApp();
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * PCシェル (admin / viewer / sales)
 * Phase 2-B-4: sales 対応。sales のナビは P7 のみ、初期画面も P7。
 */
function renderPcShell(member) {
  userNameEl().textContent = member.name || '(名前未設定)';
  userRoleEl().textContent = ROLE_LABEL[member.role] || member.role || '';

  const palette = FALLBACK_AVATAR_COLORS[member.avatarColor] || FALLBACK_AVATAR_COLORS.slate;
  const initial = (member.name || '?').slice(0, 1);
  const av = userAvatarEl();
  av.textContent = initial;
  av.style.color = palette.text;
  av.style.backgroundColor = palette.bg;

  const isAdmin = member.role === 'admin';
  const isSales = member.role === 'sales';

  // admin 専用ナビ
  ['nav-p3', 'nav-p2', 'nav-p4', 'nav-p6'].forEach((id) => {
    const el = $(id);
    if (el) el.style.display = isAdmin ? 'inline-block' : 'none';
  });

  // P1 (管理ダッシュボード) / P7 (営業ダッシュボード) は admin / sales の両方に表示
  // (P7 の admin は KPI 対象外のため左カラムが「チーム」表示になる)
  ['nav-p1', 'nav-p7'].forEach((id) => {
    const el = $(id);
    if (el) el.style.display = (isAdmin || isSales) ? 'inline-block' : 'none';
  });

  // Phase 2-B-5: ホーム (天気 + 名言) は admin / sales 共通で表示
  const homeBtn = $('nav-home');
  if (homeBtn) homeBtn.style.display = 'inline-block';

  // モバイル切替ボタン: admin / sales 両方に表示
  const switchBtn = $('switch-to-mobile-btn');
  if (switchBtn) switchBtn.style.display = (isAdmin || isSales) ? 'inline-block' : 'none';

  // Phase 2-B-5: 初期画面はホーム。5秒後に admin -> P1 / sales -> P7 へ自動遷移
  navigateTo('home', { autoRedirect: true });
}

function navigateTo(target, opts = {}) {
  // 全ページを非表示
  NAV_ITEMS.forEach((key) => {
    const wrap = $(`${key}-content-wrapper`) || $(`${key}-content`);
    if (wrap) wrap.style.display = 'none';
  });

  // 対象を表示
  if (target === 'home') {
    const c = $('home-content');
    c.style.display = 'block';
    // Phase 2-B-5: 天気 + 名言。autoRedirect は初回シェル描画時のみ true
    renderHomePage(c, activeMember(), {
      autoRedirect: opts.autoRedirect === true,
      redirectTarget:
        activeMember() && activeMember().role === 'sales' ? 'p7' : 'p1',
      onNavigate: navigateTo
    });
  } else if (target === 'p1') {
    const c = $('p1-content-wrapper');
    c.style.display = 'block';
    renderDashboardPage(c);
  } else if (target === 'p7') {
    const c = $('p7-content-wrapper');
    c.style.display = 'block';
    // 第3引数 = 認証ユーザー本人 (プレビュー中でも書き込みは本人名義で行うため)
    renderSalesDashboardPage(c, activeMember(), currentSessionMember);
  } else if (target === 'p3') {
    const c = $('p3-content-wrapper');
    c.style.display = 'block';
    renderMembersPage(c, activeMember());
  } else if (target === 'p2') {
    const c = $('p2-content-wrapper');
    c.style.display = 'block';
    renderGoalsPage(c);
  } else if (target === 'p4') {
    const c = $('p4-content-wrapper');
    c.style.display = 'block';
    renderClientsPage(c, activeMember());
  } else if (target === 'p6') {
    const c = $('p6-content-wrapper');
    c.style.display = 'block';
    renderConfigPage(c);
  }

  setActiveNav(target);
}

function setActiveNav(target) {
  NAV_ITEMS.forEach((key) => {
    const btn = $(`nav-${key}`);
    if (btn) btn.classList.toggle('is-active', key === target);
  });
}

/* ========================================================================
 * 認証イベント
 * ====================================================================== */

function bindEvents() {
  googleBtn().addEventListener('click', async () => {
    setLoginMessage('');
    googleBtn().disabled = true;
    emailLinkBtn().disabled = true;
    try {
      await signInWithGoogle();
    } catch (err) {
      console.error(err);
      setLoginMessage(humanizeAuthError(err), true);
      googleBtn().disabled = false;
      emailLinkBtn().disabled = false;
    }
  });

  emailLinkBtn().addEventListener('click', async () => {
    setLoginMessage('');
    const email = emailInput().value.trim();
    if (!email) {
      setLoginMessage('メールアドレスを入力してください', true);
      return;
    }
    emailLinkBtn().disabled = true;
    googleBtn().disabled = true;
    try {
      await sendMagicLink(email);
      setLoginMessage(`${email} にログインリンクを送信しました。メールを確認してください。`);
    } catch (err) {
      console.error(err);
      setLoginMessage(humanizeAuthError(err), true);
    } finally {
      emailLinkBtn().disabled = false;
      googleBtn().disabled = false;
    }
  });

  signOutBtn().addEventListener('click', async () => {
    try {
      await signOutChibariyo();
    } catch (err) {
      console.error(err);
      alert('サインアウトに失敗しました: ' + err.message);
    }
  });

  NAV_ITEMS.forEach((key) => {
    const btn = $(`nav-${key}`);
    if (btn) btn.addEventListener('click', () => navigateTo(key));
  });

  // Phase 2-B-2.1: PC → モバイル切替 (admin / sales でボタン表示)
  // Phase 2-B-5.1: プレビュー中は pref を触らず、対象メンバーのモバイルプレビューへ戻す
  const switchBtn = $('switch-to-mobile-btn');
  if (switchBtn) {
    switchBtn.addEventListener('click', () => {
      if (previewMember) {
        gotoPreview(previewMember.memberId, null);
        return;
      }
      if (!currentSessionMember) return;
      setShellPref(currentSessionMember.memberId, 'mobile');
      window.location.reload();
    });
  }
}

function humanizeAuthError(err) {
  if (err instanceof ChibariyoAuthError) return err.message;
  const code = err && err.code ? err.code : '';
  switch (code) {
    case 'auth/popup-closed-by-user':   return 'ログインがキャンセルされました。';
    case 'auth/popup-blocked':          return 'ポップアップがブロックされました。ブラウザ設定で許可してください。';
    case 'auth/invalid-email':          return 'メールアドレスの形式が正しくありません。';
    case 'auth/network-request-failed': return 'ネットワークエラーが発生しました。';
    case 'auth/invalid-action-code':    return 'リンクが無効または期限切れです。';
    case 'auth/user-disabled':          return 'このアカウントは無効化されています。';
    default: return err && err.message ? err.message : 'ログインに失敗しました。';
  }
}

/* ========================================================================
 * 起動
 * ====================================================================== */

async function bootstrap() {
  registerServiceWorker();
  paintEnvBadge();
  paintVersionLabel();
  bindEvents();
  showLoading();
  await authReady;

  try {
    const result = await completeMagicLinkSignIn();
    if (result.needEmailPrompt) {
      const email = window.prompt('確認のため、リンクを送信したメールアドレスを入力してください');
      if (email) {
        try {
          await completeMagicLinkSignInWithEmail(email);
        } catch (err) {
          console.error(err);
          setLoginMessage(humanizeAuthError(err), true);
        }
      }
    }
  } catch (err) {
    console.error('[チバリヨ] メールリンク処理に失敗:', err);
    setLoginMessage(humanizeAuthError(err), true);
  }

  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      currentSessionMember = null;
      showLogin();
      googleBtn().disabled = false;
      emailLinkBtn().disabled = false;
      return;
    }

    showLoading();
    try {
      const member = await linkMemberOnLogin(user);
      currentSessionMember = member;
      applyRoleShell(member);

      // 通知 (FCM): 許可済みなら静かにトークン更新、未許可なら有効化バナーを出す。
      //   プレビュー (?as=) でも認証ユーザー本人 (member) のトークンとして扱う。
      initPush(member);
      maybeShowEnableBanner(member);

      // バージョン更新の監視 (新デプロイ検知 → 再読み込みバナー)
      startVersionWatch();
    } catch (err) {
      console.error('[チバリヨ] members 紐付けに失敗:', err);
      await signOutChibariyo();
      showLogin();
      setLoginMessage(humanizeAuthError(err), true);
    }
  });
}

bootstrap().catch((err) => {
  console.error('[チバリヨ] 起動失敗:', err);
});
